import { useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Navbar from '../../components/Navbar';
import { getPost, POSTS } from './posts';

const imgUrl = (slug) => `https://images.unsplash.com/${slug}?w=1600&h=900&fit=crop&auto=format&q=75`;

// Renders **bold** and creates paragraphs from \n\n
function renderBody(text) {
  return text.split('\n\n').map((para, i) => {
    const parts = para.split(/(\*\*[^*]+\*\*)/g).map((seg, j) => {
      if (seg.startsWith('**') && seg.endsWith('**')) {
        return <strong key={`${i}-bold-${j}-${seg.slice(2, -2).slice(0, 12)}`} className="text-white font-semibold">{seg.slice(2, -2)}</strong>;
      }
      return <span key={`${i}-seg-${j}-${seg.slice(0, 12)}`}>{seg}</span>;
    });
    return <p key={`para-${i}-${para.slice(0, 24)}`} className="mb-4 text-white/75 leading-[1.8]">{parts}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);

  // SEO: set title + meta description for each post
  useEffect(() => {
    if (!post) return;
    const oldTitle = document.title;
    document.title = `${post.title} · DIS2ORT Blog`;
    const meta = document.querySelector('meta[name="description"]');
    const oldDesc = meta?.getAttribute('content');
    if (meta) meta.setAttribute('content', post.excerpt);
    return () => {
      document.title = oldTitle;
      if (meta && oldDesc) meta.setAttribute('content', oldDesc);
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;
  const others = POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white" data-testid="blog-post">
      <Navbar />

      <article className="max-w-3xl mx-auto px-6 sm:px-10 pt-12 pb-24 relative">
        <Link to="/blog" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-8" data-testid="blog-back">
          <ArrowLeft className="w-3.5 h-3.5" /> All posts
        </Link>

        {/* Hero */}
        <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-8 border border-white/[0.06]">
          <img src={imgUrl(post.coverImage)} alt={post.title} className="w-full h-full object-cover" />
        </div>

        <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.2em] uppercase mb-4">
          <span className="px-2 py-1 rounded-full bg-[#FF6B4A]/20 text-[#FF6B4A]">{post.tag}</span>
          <span className="flex items-center gap-1 text-white/40"><Clock className="w-3 h-3" /> {post.readTime}</span>
          <span className="text-white/40">· {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>

        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight text-white leading-[1.05] mb-5" data-testid="blog-title">
          {post.title}
        </h1>
        <p className="text-base text-white/60 leading-relaxed mb-10 border-l-2 border-[#FF6B4A]/40 pl-4 italic">{post.excerpt}</p>

        {post.sections.map((s) => (
          <section key={`sec-${s.heading}`} className="mb-10">
            <h2 className="font-display text-2xl font-bold tracking-tight text-white mb-3" data-testid={`section-${s.heading}`}>
              {s.heading}
            </h2>
            <div className="text-base">{renderBody(s.body)}</div>
          </section>
        ))}

        {/* Inline CTA */}
        <div className="mt-12 p-7 rounded-2xl bg-gradient-to-br from-[#FF6B4A]/15 via-[#E040A0]/10 to-transparent border border-[#FF6B4A]/20 text-center">
          <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-[#FFD93D] mb-3">Try it yourself</p>
          <h3 className="font-display text-2xl font-bold tracking-tight text-white mb-3">150 models. One studio. 50 free credits.</h3>
          <Link to="/login">
            <Button className="bg-gradient-to-r from-[#FF6B4A] to-[#E040A0] text-white font-bold uppercase tracking-[0.15em] px-7 py-5 text-xs rounded-full hover:from-[#FF8E72] hover:to-[#FF6B9D] shadow-[0_8px_24px_rgba(255,107,74,0.3)] transition-shadow" data-testid="blog-cta-btn">
              Start free <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </div>

        {/* Other posts */}
        {others.length > 0 && (
          <div className="mt-16 pt-10 border-t border-white/[0.06]">
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-5">Keep reading</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {others.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`other-post-${p.slug}`}
                  className="group rounded-xl overflow-hidden bg-white/[0.03] border border-white/[0.06] hover:border-white/[0.15] transition-colors">
                  <div className="aspect-[3/2]">
                    <img src={imgUrl(p.coverImage)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-4">
                    <h4 className="font-display text-base font-bold text-white leading-tight line-clamp-2">{p.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>
    </div>
  );
}
